import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../contexts/LocaleContext";
import LanguageSelector from "../components/LanguageSelector";

function authErrorKey(code) {
  const map = {
    "auth/invalid-email": "invalidEmail",
    "auth/user-disabled": "userDisabled",
    "auth/user-not-found": "userNotFound",
    "auth/wrong-password": "wrongPassword",
    "auth/invalid-credential": "invalidCredential",
    "auth/email-already-in-use": "emailAlreadyInUse",
    "auth/weak-password": "weakPassword",
  };
  return map[code] || "default";
}

export default function Register() {
  const { user, loading, signUp } = useAuth();
  const { t } = useTranslation();
  const { basePath, localizePath } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get("redirect");
  const redirect = redirectParam ? `${basePath}${redirectParam}` : basePath || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate(redirect, { replace: true });
    }
  }, [user, loading, navigate, redirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    const trimmedPassword = password;
    if (!trimmedEmail || !trimmedPassword) {
      setError(t("login.errorEnterEmailPassword"));
      return;
    }
    if (trimmedPassword.length < 6) {
      setError(t("login.errorPasswordLength"));
      return;
    }
    setSubmitting(true);
    try {
      await signUp(trimmedEmail, trimmedPassword);
    } catch (err) {
      const key = authErrorKey(err.code);
      setError(t(`login.authErrors.${key}`) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh", display: "flex", alignItems: "center" }}>
      <Container maxWidth="sm">
        <Box sx={{ py: 8, position: "relative" }}>
          <Box sx={{ position: "absolute", top: 0, right: 0 }}>
            <LanguageSelector />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Cal Sans", "Inter", sans-serif',
              fontWeight: 600,
              letterSpacing: "-0.02em",
              color: "#111827",
              mb: 2,
              textAlign: "center",
            }}
          >
            RepRadar
          </Typography>
          <Typography
            sx={{
              color: "#111827",
              fontSize: "1.5rem",
              fontWeight: 600,
              mb: 0.5,
              textAlign: "center",
            }}
          >
            {t("login.createAccount")}
          </Typography>
          <Typography sx={{ color: "#6b7280", mb: 4, textAlign: "center" }}>
            {t("login.subtitleSignUp")}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              label={t("login.email")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              fullWidth
              required
            />
            <TextField
              label={t("login.password")}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              fullWidth
              required
              helperText={t("login.passwordHelper")}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{
                bgcolor: "#111827",
                textTransform: "none",
                py: 1.5,
                "&:hover": { bgcolor: "#374151" },
              }}
            >
              {submitting ? "…" : t("login.createAccount")}
            </Button>
            <Typography sx={{ textAlign: "center", color: "#6b7280", fontSize: 14 }}>
              {t("login.alreadyHaveAccount")}{" "}
              <Link
                to={localizePath("/login") + (redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : "")}
                style={{ color: "#111827", fontWeight: 600, textDecoration: "none" }}
              >
                {t("login.signIn")}
              </Link>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
